import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { authHeaders } from "./auth";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;

/**
 * Tiny thumbs-up / thumbs-down widget. Sits subtly in the corner of a render.
 * Props:
 *  - renderId: string (required)  — render document id
 *  - position?: "br" | "bl" | "tl" | "tr"  (default "bl")
 *  - variant?: "dark" | "light"  (default "dark", glassy on image)
 *  - className?: extra classes
 */
export default function FeedbackWidget({ renderId, position = "bl", variant = "dark", className = "" }) {
  const [given, setGiven] = useState(null); // "up" | "down" | null
  const [busy, setBusy] = useState(false);
  const [thanks, setThanks] = useState(false);

  if (!renderId) return null;

  const send = async (rating) => {
    if (busy || given) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/renders/${renderId}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ rating }),
      });
      if (res.ok) {
        setGiven(rating);
        setThanks(true);
        // Auto-fade the thank-you after 2.5s but keep the chosen state visible
        setTimeout(() => setThanks(false), 2500);
      }
    } catch {
      /* swallow — feedback is best-effort */
    } finally {
      setBusy(false);
    }
  };

  const pos = {
    br: "bottom-2 right-2",
    bl: "bottom-2 left-2",
    tr: "top-2 right-2",
    tl: "top-2 left-2",
  }[position];

  const base =
    variant === "dark"
      ? "bg-black/55 text-white/85 backdrop-blur hover:text-white"
      : "bg-white/90 text-heda-ink/70 hover:text-heda-ink";

  // Shows a one-line "Thanks" toast that replaces the buttons briefly
  if (thanks) {
    return (
      <div
        data-testid="feedback-thanks"
        className={[
          "absolute z-10 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium shadow-sm",
          base,
          pos,
          className,
        ].join(" ")}
      >
        <Check size={12} className="text-heda-gold" />
        Thanks for the feedback
      </div>
    );
  }

  return (
    <div
      data-testid="feedback-widget"
      className={[
        "absolute z-10 flex items-center gap-0.5 rounded-full p-0.5 text-[11px] shadow-sm",
        base,
        pos,
        className,
      ].join(" ")}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        data-testid="feedback-up"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          send("up");
        }}
        disabled={busy || !!given}
        title="Looks great"
        className={[
          "flex h-7 w-7 items-center justify-center rounded-full transition",
          given === "up" ? "bg-heda-gold text-white" : "hover:bg-white/15",
        ].join(" ")}
      >
        <ThumbsUp size={13} />
      </button>
      <button
        data-testid="feedback-down"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          send("down");
        }}
        disabled={busy || !!given}
        title="Not quite right"
        className={[
          "flex h-7 w-7 items-center justify-center rounded-full transition",
          given === "down" ? "bg-red-500/80 text-white" : "hover:bg-white/15",
        ].join(" ")}
      >
        <ThumbsDown size={13} />
      </button>
    </div>
  );
}
